import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOpenAIStatus, useOpenAIDeviceCode, useOpenAIDevicePoll, useOpenAIDisconnect } from '@/api/hooks/useOpenAI'
import { useLanguage } from '@/i18n/LanguageContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { Settings as SettingsIcon, Zap, ExternalLink, Loader2, Copy, Check, Unlink } from 'lucide-react'

export function Settings() {
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { data: status, isLoading: statusLoading } = useOpenAIStatus()
  const deviceCode = useOpenAIDeviceCode()
  const disconnect = useOpenAIDisconnect()

  const [deviceData, setDeviceData] = useState<{
    device_auth_id: string
    user_code: string
    verification_uri: string
  } | null>(null)
  const [polling, setPolling] = useState(false)
  const [copied, setCopied] = useState(false)

  const poll = useOpenAIDevicePoll(
    deviceData?.device_auth_id ?? null,
    deviceData?.user_code ?? null,
    polling,
  )

  useEffect(() => {
    if (poll.data?.status === 'authorized') {
      setPolling(false)
      setDeviceData(null)
      queryClient.invalidateQueries({ queryKey: ['openai', 'status'] })
      toast.success('ChatGPT conectado com sucesso!')
    }
    if (poll.error) {
      setPolling(false)
      setDeviceData(null)
      toast.error('Erro na conexao. Tente novamente.')
    }
  }, [poll.data?.status, poll.error])

  const handleConnect = async () => {
    try {
      const data = await deviceCode.mutateAsync()
      setDeviceData(data)
      setPolling(true)
    } catch {
      toast.error('Erro ao iniciar conexao com OpenAI')
    }
  }

  const handleDisconnect = () => {
    if (!confirm('Desconectar sua conta ChatGPT?')) return
    disconnect.mutate(undefined, {
      onSuccess: () => toast.success('ChatGPT desconectado'),
      onError: () => toast.error('Erro ao desconectar'),
    })
  }

  const handleCopy = () => {
    if (deviceData?.user_code) {
      navigator.clipboard.writeText(deviceData.user_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <SettingsIcon className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold tracking-tight">{t('Configuracoes')}</h1>
      </div>

      {/* OpenAI Integration Card */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10">
              <Zap className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">OpenAI / ChatGPT</h2>
              <p className="text-xs text-muted-foreground">
                {t('Conecte sua conta ChatGPT para usar IA no Blissf')}
              </p>
            </div>
          </div>
          {statusLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : status?.connected ? (
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10">
              {t('Conectado')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              {t('Desconectado')}
            </Badge>
          )}
        </div>

        <div className="px-5 py-4">
          {/* Connected state */}
          {status?.connected && !deviceData && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t('Conta ChatGPT conectada desde')} {status.connectedAt ? new Date(status.connectedAt).toLocaleDateString('pt-BR') : '—'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnect.isPending}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <Unlink className="h-3.5 w-3.5" />
                {t('Desconectar')}
              </Button>
            </div>
          )}

          {/* Device Code Flow */}
          {deviceData && polling && (
            <div className="space-y-4">
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <p className="text-sm font-medium">{t('Insira o codigo abaixo no site da OpenAI:')}</p>
                <div className="flex items-center gap-2">
                  <code className="rounded-md bg-background border border-border px-4 py-2 text-2xl font-bold tracking-[0.3em] select-all">
                    {deviceData.user_code}
                  </code>
                  <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0">
                    {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => window.open(deviceData.verification_uri, '_blank')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t('Abrir OpenAI')}
                </Button>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('Aguardando autorizacao...')}
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPolling(false); setDeviceData(null) }}
                className="text-xs"
              >
                {t('Cancelar')}
              </Button>
            </div>
          )}

          {/* Not connected, no flow active */}
          {!status?.connected && !deviceData && !statusLoading && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t('Conecte sua conta ChatGPT para usar modelos da OpenAI. Voce usara os creditos da sua assinatura do ChatGPT.')}
              </p>
              <Button
                size="sm"
                onClick={handleConnect}
                disabled={deviceCode.isPending}
                className="gap-1.5"
              >
                {deviceCode.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                {t('Conectar com ChatGPT')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
